import { makeAutoObservable } from 'mobx'
import RoyalRingRecipientFactory from './../../../shared/factories/royal-ring-recipient.factory'
import UserFactory from './../../../shared/factories/user.factory'
import moment from 'moment'

const searchRecipients = async ({ search, pagination, order_by }) => {
  pagination = pagination && typeof pagination === 'object' ? pagination : {}

  try {
    const searchParams = {
      order_by: order_by || { created_at: 'DESC' },
      pagination,
    }

    if (search && Object.keys(search).length > 0) {
      searchParams.search = search
    }

    return await RoyalRingRecipientFactory.search(searchParams)
  } catch (ex) {
    console.error(`Failed to search royal ring recipients: ${ex}`)
    return { models: [], pagination: {} }
  }
}

const getUsers = async ids => {
  try {
    return (
      (
        await UserFactory.search({
          search: { id: ids },
          pagination: { per_page: ids.length },
        })
      )?.models || []
    )
  } catch (ex) {
    console.error(`Failed to search users: ${ex}`)
    return []
  }
}

class RoyalRingsStore {
  isLoading = false
  isSearching = false
  isSaving = false

  // Data models
  Recipients = []
  recipientPagination = {}
  EditingRecipient = null
  Users = {}

  // Filters
  filterRingType = null // null = all, or 'ceo', 'leader', 'agent'

  // Sorting
  sortField = 'created_at'
  sortDirection = 'DESC'

  constructor() {
    makeAutoObservable(this)
  }

  init = async () => {
    this.isLoading = true
    await this.searchRecipients({})
    this.isLoading = false
  }

  searchRecipients = async ({ pagination } = {}) => {
    this.isSearching = true

    const searchParams = {}

    // Apply filters
    if (this.filterRingType) {
      searchParams.ring_type = this.filterRingType
    }

    // Build order_by object
    const order_by = {}
    order_by[this.sortField] = this.sortDirection

    const { models, pagination: pag } = await searchRecipients({
      search: searchParams,
      pagination,
      order_by,
    })

    this.Recipients = models
    this.recipientPagination = pag

    // Load user data for recipients
    if (Array.isArray(models) && models.length > 0) {
      let userIds = models
        .map(R => R.get('user_id'))
        .filter(userId => userId && !isNaN(userId))
        .filter(userId => !this.Users.hasOwnProperty(userId))
        .filter((val, idx, self) => self.indexOf(val) === idx)

      if (userIds.length > 0) {
        const users = await getUsers(userIds)
        users.forEach(User => (this.Users[User.id()] = User))
      }
    }

    this.isSearching = false
  }

  getUserNameByRecipient = Recipient => {
    if (Recipient && this.Users[Recipient.get('user_id')]) {
      return [this.Users[Recipient.get('user_id')].get('u_fname'), this.Users[Recipient.get('user_id')].get('u_lname')]
        .filter(n => n)
        .join(' ')
        .trim()
    }
    return 'Unknown User'
  }

  edit = Recipient => {
    this.EditingRecipient = Recipient
  }

  cancelEdit = () => {
    this.EditingRecipient = null
  }

  save = async () => {
    if (!this.EditingRecipient) return

    this.isSaving = true

    try {
      if (`${this.EditingRecipient.get('received_at')}`.toLowerCase().includes('z'))
        this.EditingRecipient.set(
          'received_at',
          moment(this.EditingRecipient.get('received_at')).utc().format('YYYY-MM-DD HH:mm:ss')
        )
      if (`${this.EditingRecipient.get('notified_at')}`.toLowerCase().includes('z'))
        this.EditingRecipient.set(
          'notified_at',
          moment(this.EditingRecipient.get('notified_at')).utc().format('YYYY-MM-DD HH:mm:ss')
        )
      await this.EditingRecipient.save()
      this.EditingRecipient = null
      await this.searchRecipients({ pagination: this.recipientPagination })
    } catch (ex) {
      console.error(`Failed to save recipient: ${ex}`)
    } finally {
      this.isSaving = false
    }
  }

  setFilterRingType = type => {
    this.filterRingType = type
    this.searchRecipients({ pagination: { page: 0 } })
  }

  setSorting = field => {
    // Toggle direction if same field, otherwise default to DESC
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC'
    } else {
      this.sortField = field
      this.sortDirection = 'DESC'
    }
    this.searchRecipients({ pagination: { page: 0 } })
  }
}

export default new RoyalRingsStore()
